import {
  Component,
  Host,
  h,
  Prop,
  Listen,
  EventEmitter,
  Event,
  Element,
  Watch
} from '@stencil/core';

export type UuiPanelExpansionVariant= 'large' | 'light' | 'small';

@Component({
  tag: 'uui-expansion-panel',
  styleUrl: 'uui-expansion-panel.css',
  shadow: true
})

/**
 * @slot default - Slot for the content of the uui-expansion-panel.
 */
export class UuiExpansionPanel {
  /** width of a panel */
  @Prop() width: string = 'auto';
  /** adds a background color to the panel when expanded */
  @Prop() enableExpandedBgColor: boolean = false;
  @Element() el: HTMLElement;
  /** type of panel to render */
  @Prop({ mutable: false, reflect: true }) variant: UuiPanelExpansionVariant =
    'large';
  /** panel expanded */
  @Prop({ mutable: true, reflect: true }) expanded: boolean;
  /** triggers when the panel is  expanded */
  @Event({ bubbles: true, composed: true }) expand: EventEmitter;
  /** triggers when the panel is collapsed */
  @Event({ bubbles: true, composed: true }) collapse: EventEmitter;
  @Prop({ mutable: false, reflect: false }) panelId: string;
  private resizeObserver: ResizeObserver = new ResizeObserver(() => {
   this.setBodyHeight(this.expanded);
  });


  @Listen('panelExpand')
  panelExpand(event: CustomEvent<string>) {
    if(this.panelId === event.detail) {
      this.expanded = !this.expanded;
    }
  }

  @Watch('expanded')
  onExpandedChange() {
    this.setBodyHeight(this.expanded);

    if (this.expanded) {
      this.expand.emit();
    } else {
      this.collapse.emit();
    }
  }

  setBodyHeight(expanded): boolean {
    if (!expanded) {
      this.el.style.setProperty('--uui-panel-body-height', 'var(--uui-panel-body-height-collapsed, 0)');
      return true;
    }

    const panelBody = this.el
      .getElementsByTagName('uui-expansion-panel-body')[0]
      .shadowRoot.querySelector('#uui-expansion-body');

    const height = `${panelBody?.getBoundingClientRect()?.height ? panelBody.getBoundingClientRect().height : 0}px`;

    // body hasn't rendered yet so unable to access its height
    if (height === '0px') return false;

    this.el.style.setProperty('--uui-panel-body-height', height);
    return true;
  }

  componentWillLoad() {
    // Inject state in all Slotted elements
    const collection = this.el.children;
    for (const element of collection) {
      if (this.expanded) {
        element.setAttribute('expanded', 'expanded');
      }
      element.setAttribute('panel-id', this.panelId);
    }
  }

  componentDidUpdate() {
    // Inject state in all Slotted elements
    const collection = this.el.children;
    for (const element of collection) {
      if (this.expanded) {
        element.setAttribute('expanded', 'expanded');
      }
    }
  }

  /* 
    stencil doesn't guarantee that the component will be fully rendered when didLoad is triggered 
    this means we need a recursive function that checks and sets the height of the panel body.
    this is required for the transition to work after first render
  */
  componentDidLoad() {
    const setHeight = () => {
      const heightSet = this.setBodyHeight(this.expanded);

      if (!heightSet) {
        return requestAnimationFrame(setHeight);
      }

      const panelBody = this.el
        .getElementsByTagName('uui-expansion-panel-body')[0]
        .shadowRoot.querySelector('#uui-expansion-body');

      this.resizeObserver.observe(panelBody, {
        box: 'device-pixel-content-box'
      });
    };

    setHeight();
  }

  render() {
    var classes = this.enableExpandedBgColor ? 'panel-bg' : '';
    return (
      <Host>
        <div style={{ width: this.width }} class={classes}>
          <slot />
        </div>
      </Host>
    );
  }
}
